/**
 * @file shop-list-filter.ts
 * @description Статус сайта магазина и отборы списка «Магазины» (phase19.28
 * этап А; uiux-platform-shops §2; SB138, PL8, PL15).
 *
 * Сайт: «Отключён» (магазин выключен платформой), «Закрыт» (SB138 — в том
 * числе новый магазин до запуска), «Открыт». Отбор «Есть проблемы» —
 * только если API отдал проверку основного адреса (`primaryCheck`): без неё
 * судить не по чему, пилюля не показывается. Строки с проблемами — первыми.
 */

import { hostToUnicode } from "@chaiberi/shared";

import type { FilterChip } from "../../components/ui/FilterChips";
import type { ShopStatusMeta } from "../shop/status-meta";
import type { ShopDomainCheck, ShopListItem } from "../api-platform-shops";
import { pluralRu } from "../plural";
import { hasCheckProblem } from "./shop-domain-status";

export type SiteState = "open" | "closed" | "disabled";

export const SITE_STATE_META: Readonly<Record<SiteState, ShopStatusMeta>> = {
  open: { label: "Сайт открыт", tone: "success" },
  closed: { label: "Сайт закрыт", tone: "neutral" },
  disabled: { label: "Магазин отключён", tone: "error" },
};

export function siteState(shop: Pick<ShopListItem, "isActive" | "isClosed">): SiteState {
  if (!shop.isActive) return "disabled";
  return shop.isClosed ? "closed" : "open";
}

export type ShopFilter = "all" | "problems" | "closed" | "disabled";

/** Есть ли у списка данные о проверках адресов (поле необязательно в контракте). */
export function listHasChecks(items: readonly ShopListItem[]): boolean {
  return items.some((item) => item.primaryCheck !== undefined);
}

/** Последняя проверка основного адреса; нет — null. */
export function primaryLastCheck(shop: Pick<ShopListItem, "primaryCheck">): ShopDomainCheck | null {
  return shop.primaryCheck?.lastCheck ?? null;
}

export function shopHasProblem(shop: ShopListItem, now: Date = new Date()): boolean {
  return hasCheckProblem(primaryLastCheck(shop), now);
}

const MATCH: Readonly<Record<ShopFilter, (shop: ShopListItem, now: Date) => boolean>> = {
  all: () => true,
  problems: (shop, now) => shopHasProblem(shop, now),
  closed: (shop) => siteState(shop) === "closed",
  disabled: (shop) => siteState(shop) === "disabled",
};

/** Отбор + порядок: проблемные первыми, дальше — как пришли с сервера. */
export function filterShops(items: readonly ShopListItem[], filter: ShopFilter, now: Date = new Date()): ShopListItem[] {
  const rows = items.filter((shop) => MATCH[filter](shop, now));
  return rows
    .map((shop, index) => ({ shop, index, bad: shopHasProblem(shop, now) ? 0 : 1 }))
    .sort((a, b) => a.bad - b.bad || a.index - b.index)
    .map((row) => row.shop);
}

/** Пилюли отбора со счётчиками: «Есть проблемы» и «Отключены» — только когда есть смысл. */
export function shopFilterChips(items: readonly ShopListItem[], now: Date = new Date()): FilterChip<ShopFilter>[] {
  const count = (filter: ShopFilter) => items.filter((shop) => MATCH[filter](shop, now)).length;
  const chips: FilterChip<ShopFilter>[] = [{ value: "all", label: "Все", count: items.length }];
  if (listHasChecks(items)) chips.push({ value: "problems", label: "Есть проблемы", count: count("problems"), tone: "danger" });
  chips.push({ value: "closed", label: "Сайт закрыт", count: count("closed") });
  const disabled = count("disabled");
  if (disabled > 0) chips.push({ value: "disabled", label: "Отключены", count: disabled });
  return chips;
}

/** Текст «пусто под отбором» (§7). */
export const FILTER_EMPTY_TEXT: Readonly<Record<ShopFilter, string>> = {
  all: "Ничего не нашлось",
  problems: "Нет магазинов с проблемами — все адреса работают",
  closed: "Нет магазинов с закрытым сайтом",
  disabled: "Отключённых магазинов нет",
};

/** Главный адрес для показа: Юникод от API, иначе из punycode; нет адреса — подпись. */
export function primaryHostLabel(shop: Pick<ShopListItem, "primaryHost" | "primaryHostUnicode">, empty = "Адреса нет"): string {
  if (shop.primaryHostUnicode) return shop.primaryHostUnicode;
  return shop.primaryHost ? hostToUnicode(shop.primaryHost) : empty;
}

/** Владелец для строки списка: имя, иначе почта. */
export function ownerLabel(shop: Pick<ShopListItem, "owners">): string {
  const owner = shop.owners[0];
  if (!owner) return "Владельца нет";
  return owner.displayName || owner.email;
}

/** «+1 адрес», «+2 адреса» — сколько ещё адресов кроме главного. */
export function extraAddressesLabel(shop: ShopListItem): string | null {
  const extra = shop.domainsCount - (shop.primaryHost ? 1 : 0);
  return extra > 0 ? `+${extra} ${pluralRu(extra, ["адрес", "адреса", "адресов"])}` : null;
}
